import { useEffect, useRef } from "react";

/**
 * Aviso en la pestaña cuando hay algo pendiente en el Chat Elite (lo mismo que el número del botón del chat):
 * - el número delante del título: "(3) LAE Fincas";
 * - un punto rojo en el favicon, arriba a la derecha.
 * Cada componente que lo usa registra su número (el widget del AAFF, el botón del equipo, que puede estar dos veces:
 * móvil y escritorio) y se muestra el mayor. Con 0 en todos vuelven el título y el favicon originales.
 */
const TAMANO = 64;
const PREFIJO = /^\(\d+\+?\) /;
const valores = new Map();
let original = null;   // href del favicon de la app
let imagen = null;     // promesa con la imagen del logo
let mostrado = 0;

const enlaceFavicon = () => document.querySelector("link[rel~='icon']");

// El logo en grande (el .ico trae una imagen pequeña y escalada se ve borrosa); si no carga, el propio favicon
const LOGO = `${process.env.PUBLIC_URL || ""}/logo192.png`;
const cargar = (src) => new Promise((resolver) => {
  const img = new Image();
  img.onload = () => resolver(img);
  img.onerror = () => resolver(null);
  img.src = src;
});
const cargarImagen = (href) => {
  if (!imagen) imagen = cargar(LOGO).then((img) => img || cargar(href)); // sin ninguno: solo el punto
  return imagen;
};

/** El logo con un punto rojo (borde blanco) arriba a la derecha. */
const dibujar = (img) => {
  const canvas = document.createElement("canvas");
  canvas.width = TAMANO;
  canvas.height = TAMANO;
  const ctx = canvas.getContext("2d");
  if (img) ctx.drawImage(img, 0, 0, TAMANO, TAMANO);
  const radio = 13;
  const cx = TAMANO - radio - 3;
  const cy = radio + 3;
  ctx.beginPath();
  ctx.arc(cx, cy, radio, 0, 2 * Math.PI);
  ctx.fillStyle = "#e11d48";
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();
  return canvas.toDataURL("image/png");
};

const ponerTitulo = (total) => {
  const base = document.title.replace(PREFIJO, "");
  const titulo = total ? `(${total > 99 ? "99+" : total}) ${base}` : base;
  if (document.title !== titulo) document.title = titulo;
};

const pintar = async () => {
  const total = valores.size ? Math.max(...valores.values()) : 0;
  if (total === mostrado) return;
  const habiaAviso = mostrado > 0;
  mostrado = total;
  ponerTitulo(total);

  // El favicon solo cambia al pasar de nada pendiente a algo pendiente (o al revés): el punto no lleva número
  if (habiaAviso === (total > 0)) return;
  const enlace = enlaceFavicon();
  if (!enlace) return;
  if (original === null) original = enlace.href;
  if (!total) {
    enlace.href = original;
    return;
  }
  try {
    const img = await cargarImagen(original);
    if (!mostrado) return; // ya no hay nada pendiente
    enlace.href = dibujar(img);
  } catch (_) {
    // Sin canvas (navegador muy antiguo): se queda el favicon original (el título sí lleva el número)
  }
};

export const fijarContadorFavicon = (id, numero) => {
  if (numero > 0) valores.set(id, numero);
  else valores.delete(id);
  pintar();
};

/** Muestra `numero` en el título y un punto en el favicon mientras el componente esté montado. */
export const useContadorFavicon = (numero) => {
  const id = useRef(Symbol("favicon"));
  useEffect(() => {
    fijarContadorFavicon(id.current, numero || 0);
  }, [numero]);
  useEffect(() => () => fijarContadorFavicon(id.current, 0), []);
};
